import {
    Alert,
    Button,
    Card,
    CardBody,
    CardFooter,
    CardHeader,
    CardSubtitle,
    CardText,
    ListGroup,
    ListGroupItem,
    Progress
} from "reactstrap";
import React from "react";
import useOffices from "../../../../hooks/useOffices";
import {ORDER_STATUSES} from "../../TrafficOrders/constants/order";
import usePermissions from "../../../../hooks/usePermissions";
import useRoles from "../../../../hooks/useRoles";

interface CardCommonOrderProps {
    id: number;
    name: string;
    orders: CommonOrderState[];
    isOwner: boolean;
    status: number;
    date?: string;
    onInfo?: (orderId: number) => void
    onUpdate?: (commonOrderId: number) => void
    onUpdateStatus?: (commonOrderId: number, date: string) => void
    onAssign?: (orderId: number, officeId: number, commonOrderId: number) => void
}

interface CommonOrderState {
    order_id: number | null;
    office_id: number;
    funnel_name?: string;
    current_capas?: {
        daily_cap: number;
        capa_day: number;
    }
}

const CardCommonOrder: React.FC<CardCommonOrderProps> = ({
                                                             id,
                                                             name,
                                                             orders,
                                                             status,
                                                             date,
                                                             isOwner,
                                                             onInfo,
                                                             onUpdate,
                                                             onUpdateStatus,
                                                             onAssign
                                                         }) => {
    const {user_offices, hasOfficeId} = useOffices()
    const {hasPermission} = usePermissions();
    const {hasRole} = useRoles()

    const common_capa_day = orders.reduce((prev, curr) => (curr.current_capas?.capa_day || 0) + prev, 0)
    const common_daily_cap = orders.reduce((prev, curr) => (curr.current_capas?.daily_cap || 0) + prev, 0)

    const handleOrderInfoClick = (orderId: number) => {
        if (onInfo) onInfo(orderId)
    }

    const handleOrderUpdateClick = (commonOrderId: number) => {
        if (onUpdate) onUpdate(commonOrderId)
    }

    const handleOrderUpdateStatusClick = (commonOrderId: number) => {
        if (onUpdateStatus && date) onUpdateStatus(commonOrderId, date)
    }

    const handleOrderAssignClick = (orderId: number, officeId: number, commonOrderId: number) => {
        if (onAssign) onAssign(orderId, officeId, commonOrderId)
    }

    const statusValue = ORDER_STATUSES.find((st) => st.value === status);

    const canFullEdit = hasPermission("update-common-orders") && (isOwner || hasRole("superadmin"));

    return (
        <Card className="text-center">
            <CardHeader className="py-3 pb-2">
                <CardSubtitle
                    className={(canFullEdit ? "justify-content-between gap-2 text-start "
                        : "justify-content-center ") + "d-flex align-items-center font-size-20 fw-bold mb-2"} tag="h2">
                        <span className="text-truncate">
                            {name}
                        </span>
                    {canFullEdit && (
                        <Button onClick={() => handleOrderUpdateClick(id)}
                                className="d-flex p-2">
                            <i className="bx bx-pencil"></i>
                        </Button>
                    )}
                </CardSubtitle>
                <CardSubtitle>
                    <button className="border-0 bg-transparent p-0"
                            disabled={!date}
                            onClick={() => handleOrderUpdateStatusClick(id)}>
                        <span className={`badge ${statusValue?.colorClass || "bg-secondary"}`}>
                            <i className={`bx ${statusValue?.iconClass || "bx-info-circle"} font-size-16 align-middle me-2`}/>
                            {statusValue?.label || "Unknown"}
                        </span>
                    </button>
                </CardSubtitle>
            </CardHeader>
            <CardBody className="py-1 px-2">
                <ListGroup flush>
                    {orders.filter(order => !!order.order_id)
                        .map((order) => (
                            <ListGroupItem key={order.order_id}>
                                <div className="d-flex align-items-center justify-content-between gap-1">
                                    <CardText className="font-size-18 fw-bold text-truncate m-0" tag="h4">
                                        [{order.order_id}] | {order.funnel_name}
                                    </CardText>

                                    {hasOfficeId(order.office_id) && (
                                        <span className="d-flex align-items-center gap-1">
                                          {hasPermission("update-common-orders") && (<Button
                                              onClick={() => handleOrderAssignClick(order.order_id || 0, order.office_id, id)}
                                              className="d-flex p-1">
                                              <i className="bx bx-pencil"></i>
                                          </Button>)}
                                            <Button onClick={() => handleOrderInfoClick(order.order_id || 0)}
                                                    className="d-flex p-1">
                                                <i className="bx bx-info-circle"></i>
                                            </Button>
                                        </span>)}
                                </div>
                                <CardText className="mb-2 text-muted" tag="span">
                                    Capa
                                </CardText>
                                <Progress
                                    striped
                                    max={order.current_capas?.daily_cap}
                                    value={order.current_capas?.capa_day}
                                    color="info"
                                    className="my-1 fs-5 position-relative bg-secondary"
                                    style={{height: "20px"}}
                                >
                                    <span className="lh-1 font-size-12 text-white position-absolute w-100 text-center">
                                        {order.current_capas?.capa_day}/{order.current_capas?.daily_cap}
                                    </span>
                                </Progress>
                            </ListGroupItem>
                        ))}
                    {orders.filter(order => !order.order_id && hasOfficeId(order.office_id))
                        .map((order, index) => (
                            <ListGroupItem key={index}>
                                <Alert color="warning"
                                       className="d-flex align-items-center justify-content-between gap-3 m-0 text-start">
                                    <span>
                                        <strong>{user_offices.find(o => o.id === order.office_id)?.name}</strong> | Please assign an order
                                    </span>

                                    <Button className="d-flex p-1"
                                            onClick={() => handleOrderAssignClick(order.order_id || 0, order.office_id, id)}>
                                        <i className="bx bx-plus-circle"></i>
                                    </Button>
                                </Alert>
                            </ListGroupItem>
                        ))}
                </ListGroup>
            </CardBody>
            <CardFooter className="py-2 pb-3">
                <CardText className="mb-2 text-muted" tag="span">
                    Common Capa
                </CardText>

                <Progress
                    striped
                    max={common_daily_cap}
                    value={common_capa_day}
                    color="success"
                    className="my-1 fs-5 position-relative bg-secondary"
                    style={{height: "20px"}}
                >
                    <span className="lh-1 font-size-12 text-white position-absolute w-100 text-center">
                        {common_capa_day}/{common_daily_cap}
                    </span>
                </Progress>
            </CardFooter>
        </Card>
    )
}

export default CardCommonOrder;